/**
 * ============================================================================
 * REDUX STORE — wiring only. Business logic lives in slices/api, never here.
 * ============================================================================
 */

import { configureStore } from '@reduxjs/toolkit';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import dashboardReducer, {
  persistDashboardState,
} from 'src/features/dashboard/dashboardSlice';
import { metricsApi } from 'src/features/api/metricsApi';
import { lifetimeSummaryApi } from 'src/features/api/lifetimeSummaryApi';
import { metricDefinitionsApi } from 'src/features/api/metricDefinitionsApi';
import { actionItemsApi } from 'src/features/api/actionItemsApi';
import { metricCommentsApi } from 'src/features/api/metricCommentsApi';
import { permissionsApi } from 'src/features/api/permissionsApi';
import { chatAgentApi } from 'src/features/api/chatAgentApi';
import { weekNumbersApi } from 'src/features/api/weekNumbersApi';

const rootReducer = {
  dashboard: dashboardReducer,
  [metricsApi.reducerPath]: metricsApi.reducer,
  [lifetimeSummaryApi.reducerPath]: lifetimeSummaryApi.reducer,
  [metricDefinitionsApi.reducerPath]: metricDefinitionsApi.reducer,
  [metricCommentsApi.reducerPath]: metricCommentsApi.reducer,
  [actionItemsApi.reducerPath]: actionItemsApi.reducer,
  [permissionsApi.reducerPath]: permissionsApi.reducer,
  [chatAgentApi.reducerPath]: chatAgentApi.reducer,
  [weekNumbersApi.reducerPath]: weekNumbersApi.reducer,
};

/**
 * Store factory — use in tests to get an isolated store per test
 * (avoids shared state and localStorage writes across test files).
 */
export function setupStore() {
  return configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware()
        .concat(metricsApi.middleware)
        .concat(lifetimeSummaryApi.middleware)
        .concat(metricDefinitionsApi.middleware)
        .concat(metricCommentsApi.middleware)
        .concat(actionItemsApi.middleware)
        .concat(permissionsApi.middleware)
        .concat(chatAgentApi.middleware)
        .concat(weekNumbersApi.middleware),
  });
}

export const store = setupStore();

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

// Persist dashboard slice — reference equality check skips unrelated actions
let lastDashboardRef: RootState['dashboard'] | undefined;
store.subscribe(() => {
  const current = store.getState().dashboard;
  if (current !== lastDashboardRef) {
    lastDashboardRef = current;
    persistDashboardState(current);
  }
});
